<template>
  <div class="baseline-comparison-view">
    <!-- Header -->
    <div class="header">
      <div class="header-content">
        <h2>📊 Baseline Compliance Report</h2>
        <p class="subtitle">
          Current state of <strong>{{ clientCode }}</strong> vs OTL Baseline
          (main branch)
        </p>
      </div>
      <div class="header-actions">
        <button class="btn-refresh" @click="loadComparison" :disabled="loading">
          <span class="icon">🔄</span>
          Refresh
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-panel">
      <div class="spinner"></div>
      <p>Analysing baseline compliance...</p>
    </div>

    <!-- Main Content -->
    <div v-else-if="complianceData" class="comparison-content">
      <!-- NEW: Matching Alert -->
      <div v-if="hasUnmatchedPolicies && !matchingMode" class="matching-alert">
        <div class="alert-box warning">
          <div class="alert-icon">🔍</div>
          <div class="alert-content">
            <h4>Unmatched Policies Detected</h4>
            <p>
              {{ unmatchedCount }} baseline
              {{ unmatchedCount === 1 ? "policy" : "policies" }}
              couldn't be automatically matched by name. This is common with
              brownfield tenants that have existing policies with different
              naming conventions.
            </p>
            <button @click="enableMatchingMode" class="btn-enable-matching">
              🎯 Enable Smart Matching
            </button>
          </div>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="summary-section">
        <div class="summary-cards">
          <div class="summary-card total">
            <div class="card-icon">📋</div>
            <div class="card-content">
              <div class="card-value">{{ complianceData.totalCount }}</div>
              <div class="card-label">Total Policies</div>
            </div>
          </div>

          <div class="summary-card compliant">
            <div class="card-icon">✅</div>
            <div class="card-content">
              <div class="card-value">{{ complianceData.compliantCount }}</div>
              <div class="card-label">Compliant</div>
              <div class="card-percentage">{{ compliancePercentage }}%</div>
            </div>
          </div>

          <div class="summary-card deviations">
            <div class="card-icon">⚠️</div>
            <div class="card-content">
              <div class="card-value">{{ complianceData.deviationCount }}</div>
              <div class="card-label">Deviations</div>
            </div>
          </div>

          <div class="summary-card critical">
            <div class="card-icon">🔴</div>
            <div class="card-content">
              <div class="card-value">{{ complianceData.criticalCount }}</div>
              <div class="card-label">Critical</div>
            </div>
          </div>

          <div class="summary-card high">
            <div class="card-icon">🟠</div>
            <div class="card-content">
              <div class="card-value">{{ complianceData.highCount }}</div>
              <div class="card-label">High Priority</div>
            </div>
          </div>

          <div class="summary-card prerequisite">
            <div class="card-icon">📋</div>
            <div class="card-content">
              <div class="card-value">
                {{ complianceData.prerequisiteCount || 0 }}
              </div>
              <div class="card-label">Baseline Prerequisites</div>
            </div>
          </div>

          <!-- NEW: Unmatched Card -->
          <div v-if="hasUnmatchedPolicies" class="summary-card unmatched">
            <div class="card-icon">🔍</div>
            <div class="card-content">
              <div class="card-value">{{ unmatchedCount }}</div>
              <div class="card-label">Unmatched</div>
            </div>
          </div>
        </div>

        <!-- Compliance Score Bar -->
        <div class="compliance-score">
          <div class="score-label">Overall Compliance Score</div>
          <div class="score-bar-container">
            <div
              class="score-bar"
              :style="{
                width: compliancePercentage + '%',
                background: getScoreColor(compliancePercentage),
              }"
            >
              <span class="score-text">{{ compliancePercentage }}%</span>
            </div>
          </div>
          <p class="score-description">
            {{ getScoreDescription(compliancePercentage) }}
          </p>
        </div>
      </div>

      <!-- ========================================== -->
      <!-- NEW: CONFIRMED MATCHES SECTION -->
      <!-- ========================================== -->
      <div
        v-if="confirmedMatches.length > 0 && !matchingMode"
        class="confirmed-matches-section"
      >
        <div
          @click="showConfirmedMatches = !showConfirmedMatches"
          class="confirmed-matches-header"
        >
          <div class="header-left">
            <svg
              class="icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <div class="header-text">
              <h3>Confirmed Matches ({{ confirmedMatches.length }})</h3>
              <p>Policies intelligently matched and excluded from deviations</p>
            </div>
          </div>
          <svg
            class="expand-icon"
            :class="{ rotated: showConfirmedMatches }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </div>

        <!-- Confirmed Matches List (Collapsible) -->
        <transition name="expand">
          <div v-show="showConfirmedMatches" class="confirmed-matches-list">
            <div
              v-for="(match, index) in confirmedMatches"
              :key="index"
              class="confirmed-match-card"
            >
              <div class="match-content">
                <!-- Match Score Badge -->
                <div class="match-badges">
                  <span
                    class="score-badge"
                    :class="{
                      'score-excellent': match.score >= 90,
                      'score-good': match.score >= 75 && match.score < 90,
                      'score-fair': match.score < 75,
                    }"
                  >
                    {{ match.score }}% Match
                  </span>

                  <!-- Confirmed Date -->
                  <span class="date-badge">
                    Confirmed {{ formatDate(match.confirmedAt) }}
                  </span>
                </div>

                <!-- Baseline → Client -->
                <div class="match-flow">
                  <div class="policy-block baseline">
                    <div class="policy-label">BASELINE</div>
                    <div class="policy-name">{{ match.baselineName }}</div>
                  </div>

                  <svg
                    class="arrow-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    ></path>
                  </svg>

                  <div class="policy-block client">
                    <div class="policy-label">CLIENT</div>
                    <div class="policy-name">{{ match.clientName }}</div>
                  </div>
                </div>

                <div v-if="match.scores" class="score-breakdown">
                  <span
                    v-if="match.scores.conditions !== undefined"
                    class="score-item"
                  >
                    {{ getScoreLabel("conditions", match.scores) }}:
                    <strong>{{ match.scores.conditions }}%</strong>
                  </span>
                  <span
                    v-if="match.scores.grantControls !== undefined"
                    class="score-item"
                  >
                    {{ getScoreLabel("grantControls", match.scores) }}:
                    <strong>{{ match.scores.grantControls }}%</strong>
                  </span>
                  <span
                    v-if="
                      match.scores.sessionControls !== undefined &&
                      match.scores.sessionControls < 100
                    "
                    class="score-item"
                  >
                    {{ getScoreLabel("sessionControls", match.scores) }}:
                    <strong>{{ match.scores.sessionControls }}%</strong>
                  </span>
                </div>
              </div>

              <!-- Delete Button -->
              <button
                @click="unmatchPolicy(match)"
                class="btn-unmatch"
                title="Remove this match"
              >
                <svg
                  class="icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </transition>
      </div>
      <!-- ========================================== -->
      <!-- END: CONFIRMED MATCHES SECTION -->
      <!-- ========================================== -->

      <!-- NEW: Matching Mode UI -->
      <div v-if="matchingMode" class="matching-mode">
        <div class="matching-header">
          <div class="header-left">
            <h3>🎯 Intelligent Policy Matching</h3>
            <p>
              Review suggested matches for policies with different names but
              similar configurations
            </p>
          </div>
          <div class="header-actions">
            <button
              @click="autoMatchHighConfidence"
              class="btn-auto-match"
              :disabled="!hasHighConfidenceMatches"
            >
              ⚡ Auto-match High Confidence (≥90%)
            </button>
            <button
              @click="saveAndExit"
              class="btn-save-exit"
              :disabled="confirmedMatches.length === 0"
            >
              💾 Save & Exit
            </button>
            <button @click="disableMatchingMode" class="btn-exit">
              ← Exit Without Saving
            </button>
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="matching-progress">
          <div class="progress-info">
            <span
              >{{ confirmedMatches.length }} of
              {{ unmatchedPolicies.length }} policies matched</span
            >
            <span class="progress-percent">{{ matchingProgressPercent }}%</span>
          </div>
          <div class="progress-bar-container">
            <div
              class="progress-bar"
              :style="{ width: matchingProgressPercent + '%' }"
            ></div>
          </div>
        </div>

        <!-- Unmatched Policies List -->
        <div class="unmatched-policies-list">
          <div v-if="unmatchedPolicies.length === 0" class="all-matched">
            <div class="success-icon">✅</div>
            <h4>All Policies Matched!</h4>
            <p>All baseline policies have been matched to client policies.</p>
            <button @click="saveAndExit" class="btn-primary">
              Save & Exit Matching Mode
            </button>
          </div>

          <div
            v-for="unmatched in unmatchedPolicies"
            :key="unmatched.path"
            class="unmatched-policy-item"
          >
            <!-- Baseline Policy -->
            <div class="baseline-policy-card">
              <div class="policy-header">
                <h4>📋 BASELINE POLICY</h4>
                <span class="policy-name">{{ unmatched.baselineName }}</span>
              </div>
              <div class="policy-summary">
                <!-- Conditional Access Conditions -->
                <div
                  v-if="unmatched.baselinePolicy?.conditions"
                  class="summary-section"
                >
                  <strong>Conditions:</strong>
                  <ul>
                    <li
                      v-if="
                        unmatched.baselinePolicy.conditions.users?.includeUsers
                      "
                    >
                      Users:
                      {{
                        formatArray(
                          unmatched.baselinePolicy.conditions.users.includeUsers
                        )
                      }}
                    </li>
                    <li
                      v-if="
                        unmatched.baselinePolicy.conditions.applications
                          ?.includeApplications
                      "
                    >
                      Apps:
                      {{
                        formatArray(
                          unmatched.baselinePolicy.conditions.applications
                            .includeApplications
                        )
                      }}
                    </li>
                    <li
                      v-if="unmatched.baselinePolicy.conditions.clientAppTypes"
                    >
                      Client Types:
                      {{
                        formatArray(
                          unmatched.baselinePolicy.conditions.clientAppTypes
                        )
                      }}
                    </li>
                  </ul>
                </div>
                <!-- Grant Controls -->
                <div
                  v-if="unmatched.baselinePolicy?.grantControls"
                  class="summary-section"
                >
                  <strong>Controls:</strong>
                  <span class="control-badges">
                    <span
                      v-for="control in unmatched.baselinePolicy.grantControls
                        .builtInControls"
                      :key="control"
                      class="control-badge"
                    >
                      {{ formatControl(control) }}
                    </span>
                  </span>
                </div>
                <!-- Intune Compliance Summary -->
                <div
                  v-if="
                    unmatched.baselinePolicy?.['@odata.type']?.includes(
                      'CompliancePolicy'
                    )
                  "
                  class="summary-section"
                >
                  <strong>Compliance Requirements:</strong>
                  <ul>
                    <li v-if="unmatched.baselinePolicy.passwordRequired">
                      Password: Required (Min length:
                      {{
                        unmatched.baselinePolicy.passwordMinimumLength || "N/A"
                      }})
                    </li>
                    <li
                      v-if="
                        unmatched.baselinePolicy.securityBlockJailbrokenDevices
                      "
                    >
                      ⚠️ Block Jailbroken/Rooted Devices
                    </li>
                    <li v-if="unmatched.baselinePolicy.osMinimumVersion">
                      OS: {{ unmatched.baselinePolicy.osMinimumVersion }}+
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Suggested Matches -->
            <div class="suggested-matches-section">
              <h5>💡 Suggested Matches ({{ unmatched.suggestions.length }})</h5>

              <div
                v-if="unmatched.suggestions.length === 0"
                class="no-suggestions"
              >
                <p>No similar policies found in client configuration.</p>
                <button @click="markNoMatch(unmatched)" class="btn-no-match">
                  Mark as No Match
                </button>
              </div>

              <div
                v-for="(match, index) in unmatched.suggestions"
                :key="match.clientId"
                :class="['match-suggestion', getMatchClass(match.score)]"
              >
                <div class="match-rank">#{{ index + 1 }}</div>

                <div class="match-content">
                  <div class="match-header">
                    <div class="match-info">
                      <div
                        class="match-score-badge"
                        :class="getScoreClass(match.score)"
                      >
                        {{ match.score }}% Match
                      </div>
                      <!-- State mismatch badge -->
                      <span
                        v-if="match.stateMismatch"
                        class="state-mismatch-badge"
                        title="Policy states differ (enabled vs disabled)"
                      >
                        ⚠️ State Mismatch
                      </span>
                      <span class="match-name">{{ match.clientName }}</span>
                    </div>
                  </div>

                  <!-- ✅ CONDITIONAL SCORE BREAKDOWN -->
                  <div class="match-scores-breakdown">
                    <span
                      v-if="match.scores.conditions !== undefined"
                      class="score-item"
                      :class="getScoreClass(match.scores.conditions)"
                    >
                      {{ getScoreLabel("conditions", match.scores) }}:
                      {{ match.scores.conditions }}%
                    </span>
                    <span
                      v-if="match.scores.grantControls !== undefined"
                      class="score-item"
                      :class="getScoreClass(match.scores.grantControls)"
                    >
                      {{ getScoreLabel("grantControls", match.scores) }}:
                      {{ match.scores.grantControls }}%
                    </span>
                    <span
                      v-if="
                        match.scores.sessionControls !== undefined &&
                        match.scores.sessionControls < 100
                      "
                      class="score-item"
                      :class="getScoreClass(match.scores.sessionControls)"
                    >
                      {{ getScoreLabel("sessionControls", match.scores) }}:
                      {{ match.scores.sessionControls }}%
                    </span>
                  </div>

                  <p class="match-explanation">{{ match.explanation }}</p>

                  <div class="match-actions">
                    <button
                      @click="confirmMatch(unmatched, match)"
                      class="btn-confirm"
                    >
                      ✓ Confirm Match
                    </button>
                    <button
                      @click="viewDetailedComparison(unmatched, match)"
                      class="btn-view"
                    >
                      📋 View Detailed Comparison
                    </button>
                  </div>
                </div>
              </div>

              <button
                v-if="unmatched.suggestions.length > 0"
                @click="markNoMatch(unmatched)"
                class="btn-no-match-alt"
              >
                None of These Match
              </button>
            </div>
          </div>
        </div>

        <!-- Confirmed Matches Summary (In Matching Mode) -->
        <div
          v-if="confirmedMatches.length > 0"
          class="confirmed-matches-summary"
        >
          <div class="summary-header">
            <h4>✅ Confirmed Matches ({{ confirmedMatches.length }})</h4>
            <button
              @click="clearAllMatches"
              class="btn-clear-all"
              title="Clear all confirmed matches"
            >
              🗑️ Clear All Matches
            </button>
          </div>

          <div class="confirmed-matches-list">
            <div
              v-for="(match, index) in confirmedMatches"
              :key="index"
              class="confirmed-match-item"
            >
              <div class="match-pair">
                <span class="baseline-name">{{ match.baselineName }}</span>
                <span class="arrow">→</span>
                <span class="client-name">{{ match.clientName }}</span>
              </div>
              <div class="match-meta">
                <span class="match-score">{{ match.score }}%</span>
                <button
                  @click="undoMatch(index)"
                  class="btn-undo"
                  title="Undo this match"
                >
                  ↶
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Grouped Deviations by Category (shown when NOT in matching mode) -->
      <div v-else-if="groupedDeviations.length > 0" class="grouped-deviations">
        <div class="section-header">
          <h3>Policy Deviations by Service Area</h3>
          <div class="bulk-actions">
            <button class="btn-expand-all" @click="expandAll">
              {{ allExpanded ? "Collapse All" : "Expand All" }}
            </button>
            <button class="btn-export" @click="openExportModal">
              📥 Export Report
            </button>
          </div>
        </div>

        <div
          v-for="group in groupedDeviations"
          :key="group.category"
          class="category-group"
        >
          <div
            class="category-header"
            @click="toggleCategory(group.category)"
            :class="{ expanded: expandedCategories.has(group.category) }"
          >
            <div class="category-info">
              <img
                v-if="getCategoryIcon(group.category)"
                :src="getCategoryIcon(group.category)"
                :alt="group.category"
                class="category-icon"
              />
              <span v-else class="category-icon-fallback">📁</span>
              <span class="category-name">{{ group.category }}</span>
              <span class="category-count"
                >{{ group.deviations.length }} deviation{{
                  group.deviations.length !== 1 ? "s" : ""
                }}</span
              >
            </div>
            <div class="category-summary">
              <span
                v-if="group.criticalCount > 0"
                class="severity-indicator critical"
                :title="`${group.criticalCount} critical`"
              >
                🔴 {{ group.criticalCount }}
              </span>
              <span
                v-if="group.highCount > 0"
                class="severity-indicator high"
                :title="`${group.highCount} high`"
              >
                🟠 {{ group.highCount }}
              </span>
              <span
                v-if="group.mediumCount > 0"
                class="severity-indicator medium"
                :title="`${group.mediumCount} medium`"
              >
                🟡 {{ group.mediumCount }}
              </span>
              <!-- ✅ ADD THIS SPAN -->
              <span
                v-if="group.prerequisiteCount > 0"
                class="severity-indicator prerequisite"
                :title="`${group.prerequisiteCount} baseline prerequisites`"
              >
                📋 {{ group.prerequisiteCount }}
              </span>
              <span class="expand-icon">
                {{ expandedCategories.has(group.category) ? "▼" : "▶" }}
              </span>
            </div>
          </div>

          <!-- Deviations List (Expandable) -->
          <transition name="expand">
            <div
              v-if="expandedCategories.has(group.category)"
              class="category-content"
            >
              <div
                v-for="deviation in group.deviations"
                :key="deviation.path"
                class="deviation-item"
                @click="viewDeviationDetails(deviation)"
              >
                <div class="deviation-header">
                  <div class="deviation-title">
                    <span
                      :class="['severity-badge', deviation.highestSeverity]"
                    >
                      {{ getSeverityIcon(deviation.highestSeverity) }}
                      {{ deviation.highestSeverity?.toUpperCase() }}
                    </span>
                    <span class="policy-name">{{ deviation.policyName }}</span>
                  </div>
                  <button class="btn-view-details">View Details →</button>
                </div>

                <div class="deviation-meta">
                  <span class="meta-badge">
                    📝 {{ deviation.deviationCount }} property deviation{{
                      deviation.deviationCount !== 1 ? "s" : ""
                    }}
                  </span>
                  <span
                    v-if="hasArrayDeviations(deviation)"
                    class="meta-badge array-indicator"
                    title="Contains changes within array items"
                  >
                    📊 Array Changes
                  </span>
                </div>

                <!-- Property Preview -->
                <div
                  class="property-preview"
                  v-if="deviation.deviations?.length > 0"
                >
                  <div
                    v-for="(dev, index) in deviation.deviations.slice(0, 3)"
                    :key="index"
                    class="preview-property"
                  >
                    <span class="property-name" :title="dev.property">
                      {{ formatPropertyPath(dev.property) }}
                    </span>
                    <span :class="['severity-dot', dev.severity]"></span>
                  </div>
                  <div
                    v-if="deviation.deviations.length > 3"
                    class="more-properties"
                  >
                    +{{ deviation.deviations.length - 3 }} more properties...
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- No Deviations State -->
      <div v-else-if="!matchingMode" class="no-deviations">
        <div class="success-icon">🎉</div>
        <h3>Perfect Compliance!</h3>
        <p>All policies match the OTL baseline configuration.</p>
      </div>
      <ExportOptionsModal
        :show="showExportModal"
        :compliance-data="enrichedComplianceData"
        :grouped-deviations="groupedDeviations"
        :client-code="clientCode"
        @close="closeExportModal"
        @export-complete="handleExportComplete"
      />
    </div>

    <!-- Error State -->
    <div v-else class="error-panel">
      <div class="error-icon">❌</div>
      <h3>Failed to Load Comparison</h3>
      <p>There was an error loading the baseline comparison data.</p>
      <button class="btn-primary" @click="loadComparison">Try Again</button>
    </div>

    <!-- Policy Detail Modal -->
    <PolicyDetailModal
      v-if="showDetailModal"
      :policy-name="
        selectedDeviation?.policyName || selectedDeviation?.baselineName
      "
      :drift-details="null"
      :compliance-details="selectedDeviation"
      :client-code="clientCode"
      :branch-name="'main'"
      :repo-id="repoId"
      @close="showDetailModal = false"
      @accept-deviation="handleAcceptDeviation"
      @fix-deviation="handleFixDeviation"
    />
    <transition name="toast">
      <div v-if="toastVisible" :class="['toast-notification', toastType]">
        <div class="toast-icon">
          <span v-if="toastType === 'success'">✅</span>
          <span v-else-if="toastType === 'error'">❌</span>
          <span v-else>ℹ️</span>
        </div>
        <div class="toast-message">{{ toastMessage }}</div>
        <button class="toast-close" @click="toastVisible = false">×</button>
      </div>
    </transition>
  </div>
</template>

<script>
import { msalInstance } from "@/auth.js";
import {
  compareBaselineVsClientOptimized,
  saveMatchingConfig,
  loadMatchingConfig,
  applyMatchingConfig,
  deleteMatch,
  addAcceptedDeviations,
  loadAcceptedDeviationsConfig,
  applyAcceptedDeviationsConfig,
  createPolicyRemediation,
} from "@/azure-devops-api.js";
import PolicyDetailModal from "./PolicyDetailModal.vue";
import ExportOptionsModal from "@/views/components/ExportOptionsModal.vue";
import { DEFAULT_EXCLUDED_PROPERTIES } from "@/utils/excluded-properties.js";
import {
  findBestMatches,
  calculatePolicySimilarity,
  explainMatch,
  isSingletonPolicy,
  detectPolicyType,
} from "@/policy-similarity-matcher.js";
import { formatDate } from "@/services/date-utils.js";
import { getAccessToken } from "@/services/auth-utils.js";
import { getSeverityIcon } from "@/services/policy-utils.js";
import { createTimer } from "@/services/perf-timer.js";

export default {
  name: "BaselineComparisonView",

  components: {
    PolicyDetailModal,
    ExportOptionsModal,
  },

  props: {
    clientCode: {
      type: String,
      required: true,
    },
    repoId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      complianceData: null,
      expandedCategories: new Set(),
      showDetailModal: false,
      selectedDeviation: null,
      excludedPaths: [
        "/M365Config/Resources/Descriptions",
        "/M365Config/Resources/Office365",
        "/M365Config/Resources/PowerPlatform",
        "/M365Config/Resources/SecurityCompliance",
        "/M365Config/Resources/Planner",
        "M365Config/Resources/Intune/MobileApps",
      ],

      // NEW: Matching mode data
      matchingMode: false,
      unmatchedPolicies: [],
      confirmedMatches: [],
      showConfirmedMatches: false,
      allBaselinePolicies: [],
      allClientPolicies: [],

      toastMessage: "",
      toastVisible: false,
      toastType: "success",
      showExportModal: false,
    };
  },

  computed: {
    categoryIcons() {
      return {
        AzureAD: "entra.png",
        AzureMaintenance: "AzureMaintenance.png",
        AzurePolicy: "AzurePolicy.png",
        Defender: "Defender.png",
        Exchange: "Exchange.png",
        Forms: "Forms.png",
        Intune: "Intune.png",
        Planner: "Planner.png",
        SharePoint: "Sharepoint.png",
        Teams: "Teams.png",
        Office365: "Office365.png",
        PowerPlatform: "PowerPlatform.png",
        SecurityCompliance: "SecurityCompliance.png",
        Descriptions: "Descriptions.png",

        // Standard baseline mappings
        "Conditional Access": "entra.png",
        "Azure AD Settings": "entra.png",
        "Device Settings": "Intune.png",
        "Defender for Office 365": "Defender.png",
        "Exchange Security": "Exchange.png",
      };
    },

    enrichedComplianceData() {
      if (!this.complianceData) return null;

      return {
        ...this.complianceData,
        compliancePercentage: this.compliancePercentage, // Add the computed percentage
      };
    },

    compliancePercentage() {
      if (!this.complianceData || this.complianceData.totalCount === 0)
        return 0;

      // ✅ Exclude prerequisites from compliance calculation
      const prerequisiteCount = this.complianceData.prerequisiteCount || 0;
      const adjustedTotal = this.complianceData.totalCount - prerequisiteCount;

      if (adjustedTotal === 0) return 100; // All items are prerequisites

      return Math.round(
        (this.complianceData.compliantCount / adjustedTotal) * 100
      );
    },

    groupedDeviations() {
      if (!this.complianceData?.deviations) return [];

      // Group deviations by category
      const groups = {};

      this.complianceData.deviations.forEach((deviation) => {
        const category = deviation.category || "Other";

        if (!groups[category]) {
          groups[category] = {
            category,
            deviations: [],
            criticalCount: 0,
            highCount: 0,
            mediumCount: 0,
            lowCount: 0,
            prerequisiteCount: 0, // ✅ ADD THIS LINE
          };
        }

        groups[category].deviations.push(deviation);

        // Count by severity
        const severity = deviation.highestSeverity || deviation.severity;

        if (severity === "critical") {
          groups[category].criticalCount++;
        } else if (severity === "high") {
          groups[category].highCount++;
        } else if (severity === "medium") {
          groups[category].mediumCount++;
        } else if (severity === "prerequisite" || deviation.isPrerequisite) {
          groups[category].prerequisiteCount++; // ✅ ADD THIS LINE
        } else {
          groups[category].lowCount++;
        }
      });

      // Convert to array and sort by category name
      return Object.values(groups).sort((a, b) =>
        a.category.localeCompare(b.category)
      );
    },

    allExpanded() {
      return this.expandedCategories.size === this.groupedDeviations.length;
    },

    // NEW: Matching mode computed properties
    hasUnmatchedPolicies() {
      return this.unmatchedPolicies.length > 0;
    },

    unmatchedCount() {
      return this.unmatchedPolicies.length;
    },

    matchingProgressPercent() {
      const total =
        this.unmatchedPolicies.length + this.confirmedMatches.length;
      if (total === 0) return 100;
      return Math.round((this.confirmedMatches.length / total) * 100);
    },

    hasHighConfidenceMatches() {
      return this.unmatchedPolicies.some((unmatched) =>
        unmatched.suggestions.some((s) => s.score >= 90)
      );
    },
  },

  async mounted() {
    await this.loadComparison();
  },

  watch: {
    clientCode() {
      this.loadComparison();
    },
  },

  methods: {
    getSeverityIcon,
    getAccessToken,
    formatDate,

    async handleFixDeviation(remediationData) {
      console.log(
        "🔧 ========== BASELINE VIEW: Fix Deviation Event Received =========="
      );
      console.log("🔧 Received remediation data:", remediationData);

      this.showDetailModal = false;

      try {
        this.loading = true;

        const accessToken = await this.getAccessToken();

        // Get user email for audit trail
        const accounts = msalInstance.getAllAccounts();
        const userEmail =
          accounts.length > 0 ? accounts[0].username : "unknown@user.com";

        // Prepare remediation request with user context
        const remediationRequest = {
          requestedBy: userEmail,
          reason: "Policy remediation from baseline comparison UI",
          policies: [
            {
              policyName: remediationData.policyName,
              clientDisplayName:
                remediationData.clientPolicy?.displayName ||
                remediationData.policyName,
              category: remediationData.category,
              policyType: remediationData.policyType,
              baselinePath: remediationData.path.replace(
                `/Clients/${this.clientCode}`,
                ""
              ),
              clientPath: remediationData.path,
              deviations: remediationData.selectedDeviations.map(
                (d) => d.property
              ),
              propertiesToFix: remediationData.selectedDeviations.map((d) => ({
                property: d.property,
                baselineValue: d.baselineValue,
                currentValue: d.clientValue,
              })),
            },
          ],
        };

        console.log(
          "🔧 Calling createPolicyRemediation with:",
          remediationRequest
        );

        // Create remediation and trigger pipeline
        const result = await createPolicyRemediation(
          accessToken,
          this.repoId,
          this.clientCode,
          remediationRequest
        );

        console.log("🔧 ✅ Remediation pipeline started:", result);

        this.showToast(
          `Remediation pipeline started successfully! Build ID: ${result.buildId}. Check Azure DevOps for progress.`,
          "success"
        );

        // Note: We don't auto-open the pipeline URL because it requires Azure DevOps authentication
        // in the new tab, which would redirect the user to sign in. Users can manually navigate
        // to Azure DevOps if they want to monitor the pipeline.

        // Reload comparison after a short delay to see any immediate changes
        setTimeout(() => {
          this.loadComparison();
        }, 2000);
      } catch (error) {
        console.error("🔧 ❌ Error creating policy remediation:", error);

        let errorMessage = "Failed to start policy remediation. ";
        if (error.response) {
          errorMessage += `Server error: ${error.response.status}`;
        } else if (error.message) {
          errorMessage += error.message;
        }

        this.showToast(errorMessage, "error");
      } finally {
        this.loading = false;
      }
    },
    deduplicateMatches(matches) {
      if (!matches || matches.length === 0) return [];

      const seen = new Map();
      const deduplicated = [];

      for (const match of matches) {
        // Use baselineId as primary key, fall back to baselineName + clientId
        const key = match.baselineId
          ? match.baselineId
          : `${match.baselineName}||${match.clientId}`;

        if (!seen.has(key)) {
          seen.set(key, true);
          deduplicated.push(match);
        } else {
          console.warn(
            `⚠️ Removing duplicate match: ${match.baselineName} → ${match.clientName}`
          );
        }
      }

      const removedCount = matches.length - deduplicated.length;
      if (removedCount > 0) {
        console.log(`🧹 Removed ${removedCount} duplicate matches`);
      }

      return deduplicated;
    },

    showToast(message, type = "success") {
      this.toastMessage = message;
      this.toastType = type;
      this.toastVisible = true;

      // Auto-hide after 4 seconds
      setTimeout(() => {
        this.toastVisible = false;
      }, 6000);
    },

    getScoreLabel(key, scores) {
      // Default labels for Conditional Access
      const defaultLabels = {
        conditions: "Conditions",
        grantControls: "Controls",
        sessionControls: "Session",
      };

      // Use custom labels if available, otherwise fallback to defaults
      return scores.scoreLabels?.[key] || defaultLabels[key] || key;
    },
    async loadComparison() {
      this.loading = true;
      this.complianceData = null;

      const timer = createTimer("Baseline Comparison");

      try {
        const accessToken = await this.getAccessToken();

        console.log("Loading baseline comparison for:", this.clientCode);

        // Load the comparison data
        let comparisonData = await compareBaselineVsClientOptimized(
          accessToken,
          this.repoId,
          this.clientCode,
          "main",
          DEFAULT_EXCLUDED_PROPERTIES,
          this.excludedPaths
        );

        timer.lap("compareBaselineVsClientOptimized");

        console.log("✅ Baseline comparison loaded");

        // ✅ NEW: Load and apply accepted deviations configuration
        console.log("📂 Loading accepted deviations configuration...");

        try {
          const acceptedConfig = await loadAcceptedDeviationsConfig(
            accessToken,
            this.repoId,
            this.clientCode
          );

          if (acceptedConfig?.acceptedDeviations?.length > 0) {
            console.log(
              `🎯 Applying ${acceptedConfig.acceptedDeviations.length} accepted deviations`
            );

            // Apply the accepted deviations config to filter them out
            comparisonData = applyAcceptedDeviationsConfig(
              comparisonData,
              acceptedConfig
            );

            console.log(
              `✅ Applied accepted deviations - ${
                comparisonData.acceptedDeviationsCount || 0
              } filtered out`
            );
          } else {
            console.log("ℹ️ No accepted deviations to apply");
          }
        } catch (error) {
          console.error("⚠️ Error loading accepted deviations config:", error);
          // Continue without accepted deviations if it fails
        }
        timer.lap("loadAcceptedDeviations + apply");

        // ✅ Load and apply matching configuration
        console.log("🔍 Attempting to load matching configuration...");

        try {
          const matchingConfig = await loadMatchingConfig(
            accessToken,
            this.repoId,
            this.clientCode
          );

          console.log("📥 Matching config loaded:", matchingConfig);

          if (
            matchingConfig &&
            matchingConfig.matches &&
            matchingConfig.matches.length > 0
          ) {
            console.log(
              `🎯 Applying ${matchingConfig.matches.length} saved matches`
            );

            // Apply the matching config to filter out matched policies
            comparisonData = applyMatchingConfig(
              comparisonData,
              matchingConfig
            );

            // ✅ DEDUPLICATE: Remove duplicate matches when loading
            const originalCount = matchingConfig.matches.length;
            this.confirmedMatches = this.deduplicateMatches(
              matchingConfig.matches
            );

            if (originalCount !== this.confirmedMatches.length) {
              console.log(
                `🧹 Removed ${
                  originalCount - this.confirmedMatches.length
                } duplicates from loaded config`
              );
              // Auto-save cleaned config back to Azure DevOps
              await this.saveMatchingConfig();
            }

            console.log(
              `✅ Applied matching config - ${comparisonData.matchesAppliedCount} policies removed from deviations`
            );
          } else {
            console.log("ℹ️ No matches to apply");
            this.confirmedMatches = [];
          }
        } catch (error) {
          console.error("❌ Error loading matching config:", error);
          console.error("Error details:", error.message, error.response?.data);
          this.confirmedMatches = [];
          // Continue without matching config if it fails
        }

        timer.lap("loadMatchingConfig + apply");

        this.complianceData = comparisonData;

        // NEW: Detect unmatched policies after loading
        this.detectUnmatchedPolicies();

        timer.lap("detectUnmatchedPolicies");
        timer.end();
      } catch (error) {
        console.error("Error loading baseline comparison:", error);
        alert("Failed to load baseline comparison. Please try again.");
        timer.end();
      } finally {
        this.loading = false;
      }
    },

    detectUnmatchedPolicies() {
      if (!this.complianceData?.deviations) {
        this.unmatchedPolicies = [];
        return;
      }

      console.log("🔍 Detecting unmatched policies...");
      console.log(
        "📋 Total deviations:",
        this.complianceData.deviations.length
      );

      // ✅ Debug log - Check if client-only policies exist
      console.log(
        "🔍 clientOnlyPolicies in complianceData:",
        this.complianceData.clientOnlyPolicies
      );
      console.log(
        "🔍 clientOnlyPolicies count:",
        this.complianceData.clientOnlyPolicies?.length
      );

      // ✅ NEW LOGIC: Only consider truly unmatched policies (missing from client)
      const potentialUnmatched = this.complianceData.deviations.filter(
        (deviation) => {
          // Skip singleton/global policies
          if (
            deviation.baselinePolicy &&
            isSingletonPolicy(deviation.baselinePolicy)
          ) {
            console.log(`⏭️ Skipping singleton policy:`, deviation.policyName);
            return false;
          }

          // ✅ ONLY include policies that are MISSING (no client file found)
          if (deviation.changeType === "missing") {
            console.log(`✅ Including missing policy:`, deviation.policyName);
            return true;
          }

          // ✅ EXCLUDE policies that already have a client match (matched by filename)
          // Even if they have deviations, they're not "unmatched"
          if (deviation.clientPolicy) {
            console.log(
              `⏭️ Skipping already-matched policy:`,
              deviation.policyName,
              "(has client counterpart)"
            );
            return false;
          }

          return false;
        }
      );

      console.log(
        `Found ${potentialUnmatched.length} truly unmatched policies (missing from client)`
      );

      // ✅ Build matching pool from BOTH matched deviations AND client-only policies
      const allClientPolicies = [];
      const clientPolicyMap = new Map();

      // Add client policies from deviations (policies that were matched by filename)
      this.complianceData.deviations.forEach((deviation) => {
        if (deviation.clientPolicy) {
          const policyType = detectPolicyType(deviation.clientPolicy);
          console.log(
            `  📄 Deviation policy: ${
              deviation.policyName
            }, Type: ${policyType}, Singleton: ${isSingletonPolicy(
              deviation.clientPolicy
            )}`
          );

          if (!isSingletonPolicy(deviation.clientPolicy)) {
            const id =
              deviation.clientPolicy.id || deviation.clientPolicy.displayName;
            if (id && !clientPolicyMap.has(id)) {
              clientPolicyMap.set(id, deviation.clientPolicy);
              allClientPolicies.push(deviation.clientPolicy);
              console.log(
                `    ✅ Added deviation policy to pool: ${deviation.policyName} (${policyType})`
              );
            }
          }
        }
      });

      console.log(
        "📊 Client policies from deviations:",
        allClientPolicies.length
      );

      // ✅ Add client-only policies (policies that exist in client but not baseline)
      if (this.complianceData.clientOnlyPolicies) {
        console.log(
          "➕ Processing client-only policies:",
          this.complianceData.clientOnlyPolicies.length
        );

        this.complianceData.clientOnlyPolicies.forEach((clientOnly) => {
          const policyType = clientOnly.clientPolicy
            ? detectPolicyType(clientOnly.clientPolicy)
            : "unknown";
          const isSingleton = clientOnly.clientPolicy
            ? isSingletonPolicy(clientOnly.clientPolicy)
            : false;

          console.log("  - Client-only policy:", {
            name: clientOnly.policyName,
            hasPolicy: !!clientOnly.clientPolicy,
            type: policyType,
            isSingleton: isSingleton,
          });

          if (clientOnly.clientPolicy && !isSingleton) {
            const id =
              clientOnly.clientPolicy.id || clientOnly.clientPolicy.displayName;
            if (id && !clientPolicyMap.has(id)) {
              clientPolicyMap.set(id, clientOnly.clientPolicy);
              allClientPolicies.push(clientOnly.clientPolicy);
              console.log(
                `    ✅ Added client-only to pool: ${clientOnly.policyName} (${policyType})`
              );
            } else if (id) {
              console.log(
                `    ⏭️ Skipped (already in pool): ${clientOnly.policyName}`
              );
            } else {
              console.log(`    ⚠️ Skipped (no id): ${clientOnly.policyName}`);
            }
          } else if (isSingleton) {
            console.log(`    ⏭️ Skipped (singleton): ${clientOnly.policyName}`);
          } else {
            console.log(
              `    ⚠️ Skipped (no policy object): ${clientOnly.policyName}`
            );
          }
        });
      } else {
        console.warn("⚠️ No clientOnlyPolicies in complianceData!");
      }

      console.log(
        `📊 Total available client policies for matching: ${allClientPolicies.length}`
      );

      // Show breakdown by type
      const typeBreakdown = {};
      allClientPolicies.forEach((policy) => {
        const type = detectPolicyType(policy);
        typeBreakdown[type] = (typeBreakdown[type] || 0) + 1;
      });
      console.log("📊 Client policies by type:", typeBreakdown);

      // Find matches for each unmatched baseline policy
      this.unmatchedPolicies = potentialUnmatched
        .filter((deviation) => deviation.baselinePolicy)
        .map((deviation) => {
          const baselinePolicy = deviation.baselinePolicy;
          const baselineType = detectPolicyType(baselinePolicy);

          console.log(
            `🔍 Finding matches for ${baselineType}: ${
              baselinePolicy.displayName || deviation.policyName
            }`
          );

          const suggestions = findBestMatches(
            baselinePolicy,
            allClientPolicies,
            60
          );

          return {
            path: deviation.path,
            baselineName: baselinePolicy?.displayName || deviation.policyName,
            baselinePolicy: baselinePolicy,
            suggestions: suggestions.map((match) => ({
              clientId: match.policy.id,
              clientName: match.policy.displayName || match.displayName,
              score: match.scores.overall,
              scores: match.scores,
              stateMismatch: match.scores.stateMismatch,
              explanation: explainMatch(
                baselinePolicy,
                match.policy,
                match.scores
              ),
              policy: match.policy,
            })),
          };
        })
        .filter((unmatched) => unmatched.suggestions.length > 0);

      console.log(
        `✅ Unmatched policies with suggestions:`,
        this.unmatchedPolicies.length
      );

      // Show which policies found matches
      this.unmatchedPolicies.forEach((unmatched) => {
        console.log(
          `  ✅ ${unmatched.baselineName}: ${unmatched.suggestions.length} suggestions`
        );
      });
    },

    extractPolicyFromDeviation(deviation) {
      // Helper to extract policy object from deviation
      // Adjust based on your actual deviation structure
      return {
        displayName: deviation.policyName,
        conditions: deviation.conditions,
        grantControls: deviation.grantControls,
        sessionControls: deviation.sessionControls,
        state: deviation.state,
      };
    },

    // NEW: Enable matching mode
    enableMatchingMode() {
      this.matchingMode = true;
      console.log("🎯 Matching mode enabled");
    },

    // NEW: Disable matching mode
    disableMatchingMode() {
      this.matchingMode = false;
      console.log("← Matching mode disabled");
    },

    async confirmMatch(unmatched, match) {
      console.log(
        "✅ Confirming match:",
        unmatched.baselineName,
        "→",
        match.clientName
      );

      // ✅ CHECK: Prevent duplicates by baselineId or baselineName
      const isDuplicate = this.confirmedMatches.some(
        (m) =>
          (m.baselineId && m.baselineId === unmatched.baselinePolicy?.id) ||
          (m.baselineName === unmatched.baselineName &&
            m.clientId === match.clientId)
      );

      if (isDuplicate) {
        console.warn("⚠️ Match already exists, skipping duplicate");
        console.warn(
          "Existing match:",
          this.confirmedMatches.find(
            (m) =>
              (m.baselineId && m.baselineId === unmatched.baselinePolicy?.id) ||
              (m.baselineName === unmatched.baselineName &&
                m.clientId === match.clientId)
          )
        );
        alert("This match has already been confirmed.");
        return;
      }

      // Add to confirmed matches
      this.confirmedMatches.push({
        baselineId: unmatched.baselinePolicy?.id,
        baselineName: unmatched.baselineName,
        clientId: match.clientId,
        clientName: match.clientName,
        score: match.score,
        scores: match.scores,
        confirmedAt: new Date().toISOString(),
      });

      // Remove from unmatched list
      const index = this.unmatchedPolicies.findIndex(
        (u) => u.path === unmatched.path
      );
      if (index > -1) {
        this.unmatchedPolicies.splice(index, 1);
      }

      // ✅ Auto-save matching config to Azure DevOps
      await this.saveMatchingConfig();
    },

    // NEW: Mark as no match
    markNoMatch(unmatched) {
      console.log("❌ Marking as no match:", unmatched.baselineName);

      // Remove from unmatched list (marking as intentionally unmatched)
      const index = this.unmatchedPolicies.findIndex(
        (u) => u.path === unmatched.path
      );
      if (index > -1) {
        this.unmatchedPolicies.splice(index, 1);
      }
    },

    // NEW: Undo a confirmed match
    async undoMatch(index) {
      const match = this.confirmedMatches[index];
      console.log("↶ Undoing match:", match.baselineName);

      try {
        const accessToken = await this.getAccessToken();

        // ✅ Delete from Azure DevOps
        await deleteMatch(
          accessToken,
          this.repoId,
          this.clientCode,
          match.baselineName
        );

        console.log("✅ Match removed from Azure DevOps");
      } catch (error) {
        console.error("Failed to delete match from Azure DevOps:", error);
      }

      // Remove from local confirmed matches
      this.confirmedMatches.splice(index, 1);

      // Re-detect to add back to unmatched
      this.detectUnmatchedPolicies();

      // Save the updated config
      await this.saveMatchingConfig();
    },

    // NEW: Auto-match high confidence
    autoMatchHighConfidence() {
      console.log("⚡ Auto-matching high confidence policies (≥90%)...");

      let matchedCount = 0;

      // Iterate through unmatched (in reverse to avoid index issues)
      for (let i = this.unmatchedPolicies.length - 1; i >= 0; i--) {
        const unmatched = this.unmatchedPolicies[i];

        // Find first suggestion with ≥90% score
        const highConfMatch = unmatched.suggestions.find((s) => s.score >= 90);

        if (highConfMatch) {
          this.confirmMatch(unmatched, highConfMatch);
          matchedCount++;
        }
      }

      console.log(`✅ Auto-matched ${matchedCount} policies`);
      alert(
        `Successfully auto-matched ${matchedCount} high-confidence policies!`
      );
    },

    // NEW: Debug helper to calculate similarity on demand
    debugPolicySimilarity(baseline, client) {
      const scores = calculatePolicySimilarity(baseline, client);
      console.log("🔍 Policy Similarity Scores:", scores);
      return scores;
    },

    viewDetailedComparison(unmatched, match) {
      console.log("📋 Viewing detailed comparison");

      const freshScores = calculatePolicySimilarity(
        unmatched.baselinePolicy,
        match.policy
      );

      // ✅ PASS excluded properties to the function
      const deviations = this.generateDeviationsFromPolicies(
        unmatched.baselinePolicy,
        match.policy,
        DEFAULT_EXCLUDED_PROPERTIES // ✅ ADD THIS
      );

      this.selectedDeviation = {
        policyName: unmatched.baselineName,
        baselineName: unmatched.baselineName,
        baselinePolicy: unmatched.baselinePolicy,
        clientPolicy: match.policy,
        similarityScores: freshScores,
        path: unmatched.path,
        deviations: deviations,
        deviationCount: deviations.length,
        highestSeverity: this.determineHighestSeverity(deviations),
      };

      this.showDetailModal = true;
    },

    generateDeviationsFromPolicies(
      baselinePolicy,
      clientPolicy,
      excludedProperties = []
    ) {
      const deviations = [];

      // Helper to check if a property path should be excluded
      const shouldExcludeProperty = (propertyPath) => {
        if (!propertyPath) return false;

        const lowerPath = propertyPath.toLowerCase();

        return excludedProperties.some((excluded) => {
          const lowerExcluded = excluded.toLowerCase();

          // Exact match
          if (lowerPath === lowerExcluded) return true;

          // Path ends with excluded property
          if (lowerPath.endsWith(`.${lowerExcluded}`)) return true;

          // Handle array indices - remove them before comparing
          const pathWithoutArrays = lowerPath.replace(/\[([^\]]+)\]/g, "");
          const excludedWithoutArrays = lowerExcluded.replace(
            /\[([^\]]+)\]/g,
            ""
          );

          if (pathWithoutArrays === excludedWithoutArrays) return true;
          if (pathWithoutArrays.endsWith(`.${excludedWithoutArrays}`))
            return true;

          // Parent path matching
          if (lowerPath.startsWith(`${lowerExcluded}.`)) return true;

          return false;
        });
      };

      const compareObjects = (baseline, client, path = "") => {
        if (!baseline && !client) return;

        if (typeof baseline !== "object" || typeof client !== "object") {
          // Primitive comparison
          if (baseline !== client) {
            // ✅ CHECK if property should be excluded
            if (!shouldExcludeProperty(path) && !this.shouldSkipField(path)) {
              deviations.push({
                property: path,
                baselineValue: baseline,
                clientValue: client,
                severity: this.determineDeviationSeverity(path),
              });
            }
          }
          return;
        }

        const allKeys = new Set([
          ...Object.keys(baseline || {}),
          ...Object.keys(client || {}),
        ]);

        allKeys.forEach((key) => {
          // ✅ Skip basic metadata fields
          if (this.shouldSkipField(key)) return;

          const newPath = path ? `${path}.${key}` : key;

          // ✅ CHECK if full path should be excluded
          if (shouldExcludeProperty(newPath)) {
            console.log(`⏭️ Skipping excluded property: ${newPath}`);
            return;
          }

          const baseValue = baseline?.[key];
          const clientValue = client?.[key];

          if (Array.isArray(baseValue) && Array.isArray(clientValue)) {
            if (JSON.stringify(baseValue) !== JSON.stringify(clientValue)) {
              deviations.push({
                property: newPath,
                baselineValue: baseValue,
                clientValue: clientValue,
                severity: this.determineDeviationSeverity(newPath),
              });
            }
          } else if (
            typeof baseValue === "object" &&
            typeof clientValue === "object"
          ) {
            compareObjects(baseValue, clientValue, newPath);
          } else if (baseValue !== clientValue) {
            deviations.push({
              property: newPath,
              baselineValue: baseValue,
              clientValue: clientValue,
              severity: this.determineDeviationSeverity(newPath),
            });
          }
        });
      };

      compareObjects(baselinePolicy, clientPolicy);
      return deviations;
    },

    // ✅ NEW: Determine severity based on property path
    determineDeviationSeverity(propertyPath) {
      const path = propertyPath.toLowerCase();

      // Critical
      if (
        path.includes("state") ||
        path.includes("block") ||
        path.includes("grantcontrols")
      ) {
        return "critical";
      }

      // High
      if (
        path.includes("conditions") ||
        path.includes("users") ||
        path.includes("applications")
      ) {
        return "high";
      }

      // Medium
      if (path.includes("session")) {
        return "medium";
      }

      return "low";
    },

    // ✅ NEW: Determine highest severity from deviations array
    determineHighestSeverity(deviations) {
      if (deviations.some((d) => d.severity === "critical")) return "critical";
      if (deviations.some((d) => d.severity === "high")) return "high";
      if (deviations.some((d) => d.severity === "medium")) return "medium";
      return "low";
    },

    // ✅ NEW: Fields to skip during comparison
    shouldSkipField(key) {
      const skipFields = [
        "id",
        "createdDateTime",
        "modifiedDateTime",
        "lastModifiedDateTime",
        "creationSource",
        "@odata.context",
        "version",
        "Guid",
        "Identity",
        "OrganizationId",
        "ExchangeVersion",
        "ObjectCategory",
        "DistinguishedName",
      ];
      return skipFields.includes(key);
    },

    async saveMatchingConfig() {
      if (this.confirmedMatches.length === 0) {
        console.log("No matches to save");
        return;
      }

      // ✅ DEDUPLICATE: Clean up before saving
      const originalCount = this.confirmedMatches.length;
      this.confirmedMatches = this.deduplicateMatches(this.confirmedMatches);

      if (originalCount !== this.confirmedMatches.length) {
        console.log(
          `🧹 Removed ${
            originalCount - this.confirmedMatches.length
          } duplicates before saving`
        );
      }

      const config = {
        version: "1.0",
        clientCode: this.clientCode,
        timestamp: new Date().toISOString(),
        matches: this.confirmedMatches,
      };

      console.log("💾 Saving matching configuration to Azure DevOps:", config);

      try {
        const accessToken = await this.getAccessToken();

        await saveMatchingConfig(
          accessToken,
          this.repoId,
          this.clientCode,
          config
        );

        console.log("✅ Matching configuration saved to Azure DevOps");

        // Also save to localStorage as backup
        localStorage.setItem(
          `matching-config-${this.clientCode}`,
          JSON.stringify(config)
        );
      } catch (error) {
        console.error("Failed to save matching config to Azure DevOps:", error);

        // Fallback to localStorage only
        try {
          localStorage.setItem(
            `matching-config-${this.clientCode}`,
            JSON.stringify(config)
          );
          console.log("⚠️ Saved to localStorage as fallback");
        } catch (localError) {
          console.error("Failed to save to localStorage:", localError);
        }
      }
    },

    async cleanupDuplicates() {
      console.log("🧹 Starting duplicate cleanup...");

      const originalCount = this.confirmedMatches.length;
      this.confirmedMatches = this.deduplicateMatches(this.confirmedMatches);
      const newCount = this.confirmedMatches.length;

      if (originalCount !== newCount) {
        await this.saveMatchingConfig();
        alert(`✅ Removed ${originalCount - newCount} duplicate matches!`);
        console.log(
          `✅ Cleanup complete: ${originalCount} → ${newCount} matches`
        );
      } else {
        alert("ℹ️ No duplicates found.");
        console.log("ℹ️ No duplicates found in current matches");
      }
    },

    // NEW: Clear all confirmed matches
    async clearAllMatches() {
      if (
        !confirm(
          `Are you sure you want to clear all ${this.confirmedMatches.length} confirmed matches?`
        )
      ) {
        return;
      }

      try {
        const accessToken = await this.getAccessToken();

        // Save an empty config to Azure DevOps
        const emptyConfig = {
          version: "1.0",
          clientCode: this.clientCode,
          timestamp: new Date().toISOString(),
          matches: [],
        };

        await saveMatchingConfig(
          accessToken,
          this.repoId,
          this.clientCode,
          emptyConfig
        );

        this.confirmedMatches = [];
        localStorage.removeItem(`matching-config-${this.clientCode}`);

        console.log("✅ All matches cleared");

        // Reload to show all unmatched policies again
        await this.loadComparison();
      } catch (error) {
        console.error("Failed to clear matches:", error);
        alert("Failed to clear matches. Please try again.");
      }
    },

    // NEW: Save and exit matching mode
    async saveAndExit() {
      await this.saveMatchingConfig();
      this.disableMatchingMode();

      // Reload comparison with matched policies applied
      await this.loadComparison();
    },

    // NEW: Format helper methods
    formatArray(arr) {
      if (!arr || arr.length === 0) return "None";
      if (arr.length === 1) return arr[0];
      if (arr.length <= 3) return arr.join(", ");
      return `${arr.slice(0, 2).join(", ")} +${arr.length - 2} more`;
    },

    formatControl(control) {
      const controlNames = {
        mfa: "MFA",
        compliantDevice: "Compliant Device",
        domainJoinedDevice: "Domain Joined",
        approvedApplication: "Approved App",
        compliantApplication: "Compliant App",
        passwordChange: "Password Change",
        block: "Block",
      };
      return controlNames[control] || control;
    },

    getMatchClass(score) {
      if (score >= 90) return "excellent";
      if (score >= 75) return "good";
      if (score >= 60) return "fair";
      return "poor";
    },

    getScoreClass(score) {
      if (score >= 90) return "score-excellent";
      if (score >= 75) return "score-good";
      if (score >= 60) return "score-fair";
      return "score-poor";
    },

    // Existing methods
    formatPropertyPath(path) {
      if (!path) return "";

      // Handle array indices like [id=1]
      let formatted = path.replace(/\[([^=]+)=([^\]]+)\]/g, " #$2");

      // Split by dots and take last 2-3 parts for brevity
      const parts = formatted.split(".");
      if (parts.length > 3) {
        formatted = "..." + parts.slice(-2).join(" → ");
      } else {
        formatted = parts.join(" → ");
      }

      // Capitalize and clean up
      formatted = formatted
        .replace(/([A-Z])/g, " $1")
        .replace(/[_-]/g, " ")
        .trim();

      // Limit length
      if (formatted.length > 50) {
        formatted = formatted.substring(0, 47) + "...";
      }

      return formatted;
    },

    hasArrayDeviations(deviation) {
      if (!deviation.deviations) return false;
      return deviation.deviations.some(
        (dev) => dev.property && dev.property.includes("[")
      );
    },

    toggleCategory(category) {
      if (this.expandedCategories.has(category)) {
        this.expandedCategories.delete(category);
      } else {
        this.expandedCategories.add(category);
      }
      this.$forceUpdate();
    },

    expandAll() {
      if (this.allExpanded) {
        this.expandedCategories.clear();
      } else {
        this.groupedDeviations.forEach((group) => {
          this.expandedCategories.add(group.category);
        });
      }
      this.$forceUpdate();
    },

    getCategoryIcon(category) {
      const iconName = this.categoryIcons[category];
      if (iconName) {
        try {
          return require(`@/assets/${iconName}`);
        } catch (e) {
          console.warn(`Icon not found for category: ${category}`);
          return null;
        }
      }
      return null;
    },

    getScoreColor(percentage) {
      if (percentage >= 90) return "linear-gradient(90deg, #28a745, #20c997)";
      if (percentage >= 75) return "linear-gradient(90deg, #ffc107, #fd7e14)";
      return "linear-gradient(90deg, #dc3545, #c82333)";
    },

    getScoreDescription(percentage) {
      if (percentage >= 90)
        return "Excellent! Your configuration closely follows the baseline.";
      if (percentage >= 75)
        return "Good compliance with some deviations to review.";
      if (percentage >= 50)
        return "Moderate compliance. Review critical deviations.";
      return "Low compliance. Immediate attention required.";
    },

    viewDeviationDetails(deviation) {
      this.selectedDeviation = deviation;
      this.showDetailModal = true;
    },

    async handleAcceptDeviation(deviationData) {
      console.log("🟢 ========== BASELINE VIEW: Event Received ==========");
      console.log("🟢 Received data:", deviationData);

      this.showDetailModal = false;

      try {
        this.loading = true;

        const accessToken = await this.getAccessToken();

        await addAcceptedDeviations(
          accessToken,
          this.repoId,
          this.clientCode,
          deviationData
        );

        console.log("🟢 ✅ Deviations accepted and saved");

        this.showToast(
          `Successfully accepted ${deviationData.selectedDeviations.length} deviation(s) for ${deviationData.policyName}`,
          "success"
        );

        await this.loadComparison();
      } catch (error) {
        console.error("🟢 ❌ Error accepting deviations:", error);

        this.showToast(
          "Failed to save accepted deviations. Please try again.",
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
    async unmatchPolicy(match) {
      if (
        !confirm(
          `Remove this match?\n\nBaseline: ${match.baselineName}\nClient: ${match.clientName}`
        )
      ) {
        return;
      }

      try {
        // Remove from local array
        const index = this.confirmedMatches.findIndex(
          (m) =>
            m.baselineName === match.baselineName &&
            m.clientName === match.clientName
        );

        if (index > -1) {
          this.confirmedMatches.splice(index, 1);
        }

        // Save updated config
        await this.saveMatchingConfig();

        // Reload comparison to show the policy in unmatched again
        await this.loadComparison();

        alert(
          "✅ Match removed successfully. The policy will now appear in unmatched list."
        );
      } catch (error) {
        console.error("Error removing match:", error);
        alert("Failed to remove match. Please try again.");
      }
    },

    openExportModal() {
      // Validate that we have data to export
      if (!this.complianceData) {
        this.showToast(
          "No compliance data available to export. Please run a comparison first.",
          "error"
        );
        return;
      }

      if (!this.groupedDeviations || this.groupedDeviations.length === 0) {
        this.showToast(
          "No deviations found to export. This client is in perfect compliance!",
          "error"
        );
        return;
      }

      this.showExportModal = true;
    },

    closeExportModal() {
      this.showExportModal = false;
    },

    handleExportComplete(filename) {
      console.log("✅ Export completed successfully:", filename);

      // Optional: Add success notification
      // You could integrate with a toast notification system here
      // this.$toast.success(`Report exported: ${filename}`);

      // Optional: Track analytics
      // this.$analytics.track('report_exported', {
      //   client: this.clientCode,
      //   filename: filename,
      //   timestamp: new Date().toISOString()
      // });
    },
  },
};
</script>

<style scoped>
.baseline-comparison-view {
  background: white;
  overflow: hidden;
}

/* Header */
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.header-content h2 {
  margin: 0 0 8px 0;
  font-size: 24px;
  font-weight: 700;
}

.subtitle {
  margin: 0;
  font-size: 14px;
  opacity: 0.9;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.3);
}

.btn-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Loading */
.loading-panel {
  padding: 80px 20px;
  text-align: center;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Main Content */
.comparison-content {
  padding: 24px;
}

/* Summary Section */
.summary-section {
  margin-bottom: 32px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-card.total {
  border-color: #6c757d;
}
.summary-card.compliant {
  border-color: #28a745;
}
.summary-card.deviations {
  border-color: #ffc107;
}
.summary-card.critical {
  border-color: #dc3545;
}
.summary-card.high {
  border-color: #fd7e14;
}

.card-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.card-content {
  flex: 1;
}

.card-value {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 4px;
}

.card-label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}

.card-percentage {
  font-size: 12px;
  color: #28a745;
  font-weight: 600;
  margin-top: 4px;
}

/* Compliance Score */
.compliance-score {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
}

.score-label {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1a1a1a;
}

.score-bar-container {
  height: 32px;
  background: #e0e0e0;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 8px;
}

.score-bar {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 16px;
  transition: width 1s ease-out;
}

.score-text {
  color: white;
  font-weight: 700;
  font-size: 14px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.score-description {
  margin: 0;
  font-size: 13px;
  color: #666;
  text-align: center;
}

/* Grouped Deviations */
.grouped-deviations {
  margin-top: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 2px solid #e0e0e0;
}

.bulk-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.btn-expand-all {
  padding: 10px 20px;
  background: white;
  border: 2px solid #667eea;
  border-radius: 6px;
  color: #667eea;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-expand-all:hover {
  background: #667eea;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

/* Category Group */
.category-group {
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.2s;
}

.category-group:hover {
  border-color: #667eea;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f8f9fa;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

.category-header:hover {
  background: #e9ecef;
}

.category-header.expanded {
  background: #667eea;
  color: white;
}

.category-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.category-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  flex-shrink: 0;
}

.category-icon-fallback {
  font-size: 24px;
  flex-shrink: 0;
}

.category-name {
  font-size: 16px;
  font-weight: 600;
}

.category-count {
  font-size: 13px;
  opacity: 0.8;
  font-weight: 500;
}

.category-summary {
  display: flex;
  align-items: center;
  gap: 12px;
}

.severity-indicator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.severity-indicator.critical {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.severity-indicator.high {
  background: rgba(253, 126, 20, 0.1);
  color: #fd7e14;
}

.severity-indicator.medium {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.severity-indicator.prerequisite {
  background: rgba(108, 117, 125, 0.1);
  color: #6c757d;
}

.category-header.expanded .severity-indicator.prerequisite {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.category-header.expanded .severity-indicator {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.expand-icon {
  font-size: 12px;
  margin-left: 8px;
}

/* Category Content */
.category-content {
  border-top: 2px solid #e0e0e0;
  background: white;
}

.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Deviation Item */
.deviation-item {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s;
}

.deviation-item:last-child {
  border-bottom: none;
}

.deviation-item:hover {
  background: #f8f9fa;
}

.deviation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.deviation-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.severity-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.severity-badge.critical {
  background: #fee;
  color: #dc3545;
  border: 1px solid #dc3545;
}

.severity-badge.high {
  background: #fff3e0;
  color: #fd7e14;
  border: 1px solid #fd7e14;
}

.severity-badge.medium {
  background: #fff9e6;
  color: #ffc107;
  border: 1px solid #ffc107;
}

.severity-badge.low {
  background: #e8f5e9;
  color: #28a745;
  border: 1px solid #28a745;
}

.policy-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

.btn-view-details {
  padding: 6px 12px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
}

.btn-view-details:hover {
  background: #5568d3;
}

.deviation-meta {
  margin-bottom: 8px;
}

.meta-badge {
  display: inline-block;
  font-size: 12px;
  color: #666;
  background: #f0f0f0;
  padding: 4px 8px;
  border-radius: 4px;
}

/* Property Preview */
.property-preview {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  border-left: 3px solid #667eea;
  margin-top: 8px;
}

.preview-property {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #e0e0e0;
}

.preview-property:last-child {
  border-bottom: none;
}

.property-name {
  font-size: 12px;
  color: #1a1a1a;
  font-family: "Courier New", monospace;
}

.severity-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.severity-dot.critical {
  background: #dc3545;
}
.severity-dot.high {
  background: #fd7e14;
}
.severity-dot.medium {
  background: #ffc107;
}
.severity-dot.low {
  background: #28a745;
}

.more-properties {
  margin-top: 8px;
  font-size: 11px;
  color: #667eea;
  font-weight: 500;
  font-style: italic;
}

/* No Deviations */
.no-deviations {
  text-align: center;
  padding: 60px 20px;
}

.success-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.no-deviations h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #28a745;
}

.no-deviations p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

/* Error State */
.error-panel {
  text-align: center;
  padding: 60px 20px;
}

.error-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.error-panel h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #dc3545;
}

.error-panel p {
  margin: 0 0 16px 0;
  color: #666;
  font-size: 14px;
}

.btn-primary {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  background: #667eea;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  background: #5568d3;
}

/* Responsive */
@media (max-width: 768px) {
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }

  .category-info {
    flex-wrap: wrap;
    gap: 8px;
  }

  .deviation-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .btn-view-details {
    width: 100%;
  }
}

/* Add after .meta-badge styles */
.array-indicator {
  background: #e7f3ff;
  color: #0066cc;
  border: 1px solid #b3d9ff;
  margin-left: 8px;
}

.prerequisite-badge {
  background: #e7f3ff;
  color: #0066cc;
  border: 1px solid #b3d9ff;
  margin-left: 8px;
}

/* Update property-name to show it's interactive */
.property-name {
  font-size: 12px;
  color: #1a1a1a;
  font-family: "Courier New", monospace;
  cursor: help; /* Shows question mark cursor */
  transition: color 0.2s;
}

.property-name:hover {
  color: #667eea;
}

/* Matching Alert */
.matching-alert {
  margin-bottom: 24px;
}

.alert-box {
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 8px;
  border: 2px solid #ffc107;
  background: #fffbf0;
}

.alert-box.warning {
  border-color: #ff9800;
  background: #fff3e0;
}

.alert-icon {
  font-size: 32px;
  flex-shrink: 0;
}

.alert-content h4 {
  margin: 0 0 8px 0;
  color: #1a1a1a;
  font-size: 16px;
}

.alert-content p {
  margin: 0 0 12px 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.btn-enable-matching {
  padding: 10px 20px;
  background: #ff9800;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-enable-matching:hover {
  background: #f57c00;
  transform: translateY(-1px);
}

/* Unmatched Summary Card */
.summary-card.unmatched {
  border-color: #ff9800;
}

/* Matching Mode Container */
.matching-mode {
  background: #f8f9fa;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px;
  margin-top: 24px;
}

.matching-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
}

.header-left h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #1a1a1a;
}

.header-left p {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.matching-header .header-actions {
  display: flex;
  gap: 12px;
}

.btn-auto-match,
.btn-exit {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-auto-match {
  background: #10b981;
  color: white;
}

.btn-auto-match:hover:not(:disabled) {
  background: #059669;
}

.btn-auto-match:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-exit {
  background: white;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-exit:hover {
  background: #667eea;
  color: white;
}

/* Matching Progress */
.matching-progress {
  margin-bottom: 24px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

.progress-percent {
  color: #10b981;
}

.matching-progress .progress-bar-container {
  height: 24px;
  background: #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}

.matching-progress .progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #059669);
  transition: width 0.3s ease;
}

/* Unmatched Policies List */
.unmatched-policies-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.unmatched-policy-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
}

/* Baseline Policy Card */
.baseline-policy-card {
  background: #f8f9fa;
  padding: 16px;
  border-radius: 6px;
  border: 2px solid #667eea;
  height: 100%;
  position: static;
  min-width: 0; /* ✅ ADD: Allow shrinking */
  overflow: hidden; /* ✅ ADD: Prevent overflow */
}

.baseline-policy-card .policy-header {
  margin-bottom: 12px;
}

.baseline-policy-card .policy-header h4 {
  margin: 0 0 4px 0;
  font-size: 12px;
  color: #667eea;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.baseline-policy-card .policy-header .policy-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  word-break: break-word;
  line-height: 1.4;
  overflow-wrap: break-word;
  white-space: normal; /* ✅ ADD: Allow wrapping */
  display: block;
}

.policy-summary {
  font-size: 13px;
}

.policy-summary .summary-section {
  margin-bottom: 12px;
}

.policy-summary .summary-section ul {
  margin: 4px 0 0 0;
  padding-left: 20px;
}

.policy-summary .summary-section li {
  margin-bottom: 4px;
  color: #666;
}

.control-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

.control-badge {
  padding: 4px 10px;
  background: #667eea;
  color: white;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

/* Suggested Matches Section */
.suggested-matches-section h5 {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #666;
}

.match-suggestion {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: white;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.2s;
  min-height: 140px; /* ✅ CHANGE: From 120px to 140px for long names */
  align-items: flex-start;
}

.match-suggestion:hover {
  border-color: #667eea;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.1);
}

.match-suggestion.excellent {
  border-color: #10b981;
  background: #f0fdf4;
}

.match-suggestion.good {
  border-color: #3b82f6;
  background: #eff6ff;
}

.match-suggestion.fair {
  border-color: #f59e0b;
  background: #fffbeb;
}

.match-rank {
  flex-shrink: 0;
  width: 36px; /* ✅ CHANGE from 32px to 36px */
  height: 36px; /* ✅ CHANGE from 32px to 36px */
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 50%;
  font-weight: 700;
  font-size: 14px;
  color: #666;
  margin-top: 4px; /* ✅ ADD: Align with first line of text */
}

.match-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px; /* ✅ ADD: Consistent spacing */
  min-width: 0; /* ✅ ADD: Allow text wrapping */
}

.match-content .match-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px; /* ✅ ADD: Small margin for spacing */
  width: 100%; /* ✅ ADD: Full width */
  min-height: 28px; /* ✅ ADD: Minimum height for badges */
}

/* Match Info - Better wrapping */
.match-info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
  max-width: 100%; /* ✅ ADD: Prevent overflow */
}

/* Match Score Badge - Consistent sizing */
.match-score-badge {
  padding: 6px 12px; /* ✅ CHANGE: More consistent padding */
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  white-space: nowrap; /* ✅ ADD: Prevent wrapping */
  align-self: flex-start; /* ✅ ADD: Align to top */
}

.match-score-badge.score-excellent {
  background: #10b981;
  color: white;
}

.match-score-badge.score-good {
  background: #3b82f6;
  color: white;
}

.match-score-badge.score-fair {
  background: #f59e0b;
  color: white;
}

.match-score-badge.score-poor {
  background: #ef4444;
  color: white;
}

.match-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  line-height: 1.4;
  word-break: break-word;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  white-space: normal; /* ✅ CHANGE: Allow wrapping instead of ellipsis */
  display: block; /* ✅ CHANGE: From -webkit-box to block */
  overflow: visible;
}

.match-scores-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 6px; /* ✅ CHANGE: Smaller gap for tighter layout */
  font-size: 0.75rem;
  margin: 4px 0; /* ✅ CHANGE: Reduce vertical spacing */
  max-width: 100%; /* ✅ ADD: Prevent overflow */
}

.score-item {
  padding: 5px 10px; /* ✅ CHANGE: More consistent padding */
  background: #f0f0f0;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1.2; /* ✅ ADD: Better line height */
}

.score-item.score-excellent {
  background: #d1fae5;
  color: #065f46;
}

.score-item.score-good {
  background: #dbeafe;
  color: #1e40af;
}

.score-item.score-fair {
  background: #fef3c7;
  color: #92400e;
}

.score-item.score-poor {
  background: #fee2e2;
  color: #991b1b;
}

.match-explanation {
  margin: 8px 0;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  white-space: pre-line;
}

.match-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px; /* ✅ CHANGE from 12px to 4px */
  padding-top: 8px; /* ✅ ADD: Visual separation */
  border-top: 1px solid #f0f0f0; /* ✅ ADD: Visual separation */
}

.btn-confirm,
.btn-view {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-confirm {
  background: #10b981;
  color: white;
}

.btn-confirm:hover {
  background: #059669;
}

.btn-view {
  background: #f0f0f0;
  color: #1a1a1a;
}

.btn-view:hover {
  background: #e0e0e0;
}

.no-suggestions {
  padding: 20px;
  text-align: center;
  color: #666;
}

.btn-no-match,
.btn-no-match-alt {
  padding: 10px 20px;
  background: white;
  color: #ef4444;
  border: 2px solid #ef4444;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  margin-top: 12px;
}

.btn-no-match:hover,
.btn-no-match-alt:hover {
  background: #ef4444;
  color: white;
}

/* Confirmed Matches */
.confirmed-matches-summary {
  background: #f0fdf4;
  padding: 20px;
  border-radius: 8px;
  border: 2px solid #10b981;
  margin-top: 24px;
}

.confirmed-matches-summary h4 {
  margin: 0 0 16px 0;
  color: #10b981;
}

.confirmed-matches-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.confirmed-match-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border-radius: 6px;
  border: 1px solid #10b981;
}

.match-pair {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
}

.baseline-name {
  font-weight: 600;
  color: #1a1a1a;
}

.arrow {
  color: #10b981;
  font-weight: 700;
}

.client-name {
  color: #666;
}

.match-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.match-score {
  font-size: 13px;
  font-weight: 600;
  color: #10b981;
}

.btn-undo {
  padding: 4px 8px;
  background: #fee;
  color: #ef4444;
  border: 1px solid #ef4444;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-undo:hover {
  background: #ef4444;
  color: white;
}

.all-matched {
  text-align: center;
  padding: 40px 20px;
}

.all-matched .success-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.all-matched h4 {
  margin: 0 0 8px 0;
  color: #10b981;
  font-size: 18px;
}

.all-matched p {
  margin: 0 0 16px 0;
  color: #666;
}

/* Responsive - Matching Mode */
@media (max-width: 1024px) {
  .unmatched-policy-item {
    grid-template-columns: 1fr;
  }

  .matching-header {
    flex-direction: column;
    gap: 16px;
  }

  .matching-header .header-actions {
    width: 100%;
  }

  .btn-auto-match,
  .btn-exit {
    flex: 1;
  }
}

@media (max-width: 768px) {
  .match-actions {
    flex-direction: column;
  }

  .btn-confirm,
  .btn-view {
    width: 100%;
  }

  .match-scores-breakdown {
    flex-direction: column;
    gap: 6px;
  }
}

.state-mismatch-badge {
  padding: 6px 10px; /* ✅ CHANGE: Match other badges */
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 12px; /* ✅ CHANGE: Match other badges */
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  align-self: flex-start; /* ✅ ADD: Align to top */
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.summary-header h4 {
  margin: 0;
  color: #10b981;
}

.btn-clear-all {
  padding: 8px 16px;
  background: #fee;
  color: #ef4444;
  border: 1px solid #ef4444;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-clear-all:hover {
  background: #ef4444;
  color: white;
}

.btn-clear-all:active {
  transform: scale(0.98);
}

/* ========================================== */
/* CONFIRMED MATCHES SECTION */
/* ========================================== */

.confirmed-matches-section {
  margin-bottom: 2rem;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 2px solid #86efac;
}

.confirmed-matches-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.confirmed-matches-header:hover {
  background: rgba(34, 197, 94, 0.1);
}

.confirmed-matches-header .header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.confirmed-matches-header .icon {
  width: 2rem;
  height: 2rem;
  color: #16a34a;
  flex-shrink: 0;
}

.confirmed-matches-header .header-text h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #14532d;
  margin: 0 0 0.25rem 0;
}

.confirmed-matches-header .header-text p {
  font-size: 0.875rem;
  color: #15803d;
  margin: 0;
}

.confirmed-matches-header .expand-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #16a34a;
  transition: transform 0.3s ease;
}

.confirmed-matches-header .expand-icon.rotated {
  transform: rotate(180deg);
}

/* Confirmed Matches List */
.confirmed-matches-list {
  padding: 0 1.5rem 1.5rem 1.5rem;
}

.confirmed-match-card {
  background: white;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  transition: all 0.2s ease;
}

.confirmed-match-card:hover {
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.15);
  transform: translateY(-1px);
}

.confirmed-match-card:last-child {
  margin-bottom: 0;
}

.confirmed-match-card .match-content {
  flex: 1;
  min-width: 0;
}

/* Match Badges */
.match-badges {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.score-badge {
  display: inline-block;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
}

.score-excellent {
  background: #dcfce7;
  color: #166534;
}

.score-good {
  background: #fef9c3;
  color: #854d0e;
}

.score-fair {
  background: #fed7aa;
  color: #9a3412;
}

.date-badge {
  font-size: 0.75rem;
  color: #15803d;
  background: rgba(34, 197, 94, 0.1);
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
}

/* Match Flow */
.match-flow {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.policy-block {
  flex: 1;
  min-width: 0;
}

.policy-label {
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.policy-block.baseline .policy-label {
  color: #7c3aed;
}

.policy-block.client .policy-label {
  color: #2563eb;
}

.policy-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.arrow-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: #16a34a;
  flex-shrink: 0;
}

/* Score Breakdown */
.score-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: #4b5563;
}

.score-breakdown .score-item strong {
  color: #1f2937;
  font-weight: 600;
}

/* Unmatch Button */
.btn-unmatch {
  padding: 0.5rem;
  background: transparent;
  border: none;
  color: #dc2626;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.btn-unmatch:hover {
  background: #fee2e2;
}

.btn-unmatch .icon {
  width: 1.25rem;
  height: 1.25rem;
}

/* Expand transition */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.btn-save-exit {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.btn-save-exit:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.btn-save-exit:disabled {
  background: #d1d5db;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-exit {
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: #6b7280;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-exit:hover {
  background: #f3f4f6;
  border-color: #d1d5db;
  color: #374151;
}

.summary-card.prerequisite {
  border-color: #6c757d;
  background: #f8f9fa;
}

.severity-badge.prerequisite {
  background: #e7f3ff;
  color: #0066cc;
  border: 1px solid #0066cc;
}

/* Toast Notification Styles */
.toast-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  min-width: 300px;
  max-width: 500px;
  padding: 16px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 10000;
  animation: slideIn 0.3s ease;
}

.toast-notification.success {
  border-left: 4px solid #28a745;
}

.toast-notification.error {
  border-left: 4px solid #dc3545;
}

.toast-notification.info {
  border-left: 4px solid #007bff;
}

.toast-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.toast-message {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
  color: #1a1a1a;
  font-weight: 500;
}

.toast-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.toast-close:hover {
  background: #f0f0f0;
  color: #666;
}

/* Toast animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.toast-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.btn-export {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-export:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

.btn-export:active {
  transform: translateY(0);
}

.deviations-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}
</style>
